import React, { useEffect, useState } from "react";
import { Box, Button, Heading, Text, Flex, Spinner, Code, Link, Divider, VStack, Show, Drawer, DrawerBody, DrawerOverlay, DrawerContent, DrawerCloseButton } from "@chakra-ui/react";
import Markdown from 'react-markdown';
import Navbar from "../NewPages/HomePage/Navbar";
import Footer from "../NewPages/HomePage/Footer";
import { HamburgerIcon } from "@chakra-ui/icons";

const DocumentationMD = () => {
    const [doc, setDoc] = useState("");
    const [loading, setLoading] = useState(true);
    const [chapters, setChapters] = useState([]);
    const [activeChapterId, setActiveChapterId] = useState("");
    const [isMobileNavOpen, setMobileNavOpen] = useState(false);

    // Initial load: Fetch sidebar.json and then the first chapter
    useEffect(() => {
        fetch("/manual/sidebar.json")
            .then((res) => {
                if (!res.ok) throw new Error("Failed to load sidebar config");
                return res.json();
            })
            .then((data) => {
                setChapters(data);
                if (data.length > 0) {
                    // Load the first chapter by default
                    loadChapter(data[0].id, data[0].file);
                }
            })
            .catch((err) => {
                console.error(err);
                setDoc("# Error\nCould not load documentation configuration.");
                setLoading(false);
            });
    }, []);

    const loadChapter = (id, filename) => {
        setLoading(true);
        setActiveChapterId(id);
        setMobileNavOpen(false);

        // Scroll to top when changing page content
        window.scrollTo(0, 0);

        fetch(`/manual/chapters/${filename}`)
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to load chapter: ${filename}`);
                return res.text();
            })
            .then((text) => {
                setDoc(text);
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setDoc(`# Error\nCould not load chapter content: ${filename}`);
                setLoading(false);
            });
    };

    // Sidebar Navigation Content
    const SidebarContent = () => (
        <VStack align="stretch" spacing={2} pb={10}>
            <Heading size="sm" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                Chapters
            </Heading>
            {chapters.map((item, index) => (
                <Box
                    key={index}
                    borderLeft="2px solid"
                    borderColor={activeChapterId === item.id ? "#f44f0d" : "transparent"}
                    bg={activeChapterId === item.id ? "orange.50" : "transparent"}
                    borderRadius="0 4px 4px 0"
                >
                    <Link
                        onClick={() => loadChapter(item.id, item.file)}
                        display="block"
                        py={2}
                        pl={3}
                        fontSize="sm"
                        fontWeight={activeChapterId === item.id ? "semibold" : "medium"}
                        color={activeChapterId === item.id ? "#f44f0d" : "gray.600"}
                        _hover={{ color: "#f44f0d", textDecoration: "none" }}
                    >
                        {item.title}
                    </Link>
                </Box>
            ))}
        </VStack>
    );

    // Custom Markdown Components
    const ChakraComponents = {
        h1: ({ children }) => (
            <Heading as="h1" size="2xl" mt={10} mb={6} color="#1a202c" letterSpacing="-0.02em">
                {children}
            </Heading>
        ),
        h2: ({ children }) => (
            <>
                <Divider my={8} borderColor="gray.200" />
                <Heading as="h2" size="xl" mt={8} mb={4} color="#2d3748" letterSpacing="-0.01em">
                    {children}
                </Heading>
            </>
        ),
        h3: ({ children }) => (
            <Heading as="h3" size="lg" mt={6} mb={3} color="#4a5568">
                {children}
            </Heading>
        ),
        p: ({ children }) => (
            <Text fontSize="lg" my={4} lineHeight="1.8" color="gray.700">
                {children}
            </Text>
        ),
        a: ({ href, children }) => (
            <Link href={href} color="#f44f0d" isExternal textDecoration="underline" fontWeight="medium">
                {children}
            </Link>
        ),
        ul: ({ children }) => (
            <Box as="ul" styleType="disc" pl={6} my={5} spacing={3} color="gray.700">
                {children}
            </Box>
        ),
        ol: ({ children }) => (
            <Box as="ol" styleType="decimal" pl={6} my={5} spacing={3} color="gray.700">
                {children}
            </Box>
        ),
        li: ({ children }) => (
            <Box as="li" fontSize="lg" pb={2} pl={1}>{children}</Box>
        ),
        blockquote: ({ children }) => (
            <Box
                as="blockquote"
                pl={6}
                py={3}
                my={8}
                borderLeft="4px solid"
                borderColor="#f44f0d"
                bg="orange.50"
                color="gray.700"
                borderRadius="r-md"
                fontSize="lg"
                fontStyle="italic"
            >
                {children}
            </Box>
        ),
        code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '')
            return !inline && match ? (
                <Box
                    as="pre"
                    p={5}
                    my={6}
                    bg="#2d3748"
                    color="gray.100"
                    borderRadius="lg"
                    overflowX="auto"
                    fontSize="sm"
                    boxShadow="sm"
                >
                    <code className={className} {...props}>
                        {children}
                    </code>
                </Box>
            ) : (
                <Code colorScheme="orange" px={2} py={0.5} fontSize="0.9em" borderRadius="md" mx={1}>
                    {children}
                </Code>
            )
        },
        img: ({ src, alt }) => (
            <Box my={8} p={1} border="1px solid" borderColor="gray.100" borderRadius="xl" boxShadow="lg" bg="white" maxW="100%">
                <Box bg="gray.50" borderRadius="lg" overflow="hidden" position="relative" minH="200px" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <img src={src} alt={alt} style={{ maxWidth: "100%", display: "block" }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                    <Flex
                        display="none"
                        flexDirection="column"
                        align="center"
                        justify="center"
                        h="100%"
                        w="100%"
                        p={10}
                        color="gray.400"
                    >
                        <Text fontSize="4xl" mb={2}>🖼️</Text>
                        <Text fontSize="lg" fontWeight="bold">Image Placeholder</Text>
                        <Text fontSize="sm">{alt}</Text>
                        <Code mt={2} fontSize="xs">{src}</Code>
                    </Flex>
                </Box>
                <Text textAlign="center" fontSize="sm" color="gray.500" mt={2} fontStyle="italic">{alt}</Text>
            </Box>
        ),
    };

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh" bg="#ffffff">
            <Navbar />

            <Flex flex="1" maxW="1440px" mx="auto" w="100%" pt={["60px", "60px", "90px"]}>

                {/* Desktop Sidebar */}
                <Show above="lg">
                    <Box
                        w="280px"
                        position="sticky"
                        top="90px"
                        h="calc(100vh - 90px)"
                        overflowY="auto"
                        borderRight="1px solid"
                        borderColor="gray.100"
                        pt={10}
                        pl={8}
                        pr={4}
                        bg="white"
                    >
                        <SidebarContent />
                    </Box>
                </Show>

                {/* Main Content */}
                <Box flex="1" minW="0" px={[4, 8, 12]} py={10}>
                    {/* Mobile Menu Button */}
                    <Show below="lg">
                        <Box mb={6} borderBottom="1px solid" borderColor="gray.100" pb={4}>
                            <Button
                                leftIcon={<HamburgerIcon />}
                                onClick={() => setMobileNavOpen(true)}
                                variant="outline"
                                size="sm"
                            >
                                Chapters
                            </Button>
                        </Box>
                    </Show>

                    {loading ? (
                        <Flex justify="center" align="center" h="50vh">
                            <Spinner size="xl" color="#f44f0d" thickness="4px" />
                        </Flex>
                    ) : (
                        <Box sx={{ "& h1:first-of-type": { marginTop: 0 } }} maxW="800px" mx="auto">
                            <Markdown components={ChakraComponents}>{doc}</Markdown>
                        </Box>
                    )}
                </Box>

            </Flex>

            {/* Mobile Navigation Drawer */}
            <Drawer isOpen={isMobileNavOpen} placement="left" onClose={() => setMobileNavOpen(false)}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerBody pt={10}>
                        <SidebarContent />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <Box bg="#f5f5f5" w="100%">
                <Footer />
            </Box>
        </Box>
    );
};

export default DocumentationMD;
